import { memo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AuraLayout, Button, HomeAddressIcon, Layout } from "lola-framework-ui-test";
import { FloatingField } from "../../components/FloatingField";
import {
  GALLERY_CARD_ORIGIN_COUNTRIES,
  LOLA_ROUTES,
  TITLE_COLOR,
  US_STATES,
} from "../../constants";
import { useStore } from "../../store/globalState";
import { countryDisplayName } from "../../utils/country-display";
import { useUsBillingZip } from "../../utils/useUsBillingZip";
import { ScrollFadeIndicator } from "../../components/ScrollFadeIndicator";
import { Navbar } from "../../components/Navbar";


export interface AddressFormFields {
  street1: string;
  street2: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
}

interface AddressFormErrors {
  street1?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  country?: string;
}

/**
 * Gallery-safe billing address after Lola add-card. Confirm goes to
 * add-card-processing — no VGS / register API.
 */
export const BillingAddressPage = memo(() => {
  const { theme } = useStore();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation("lola");

  const cardOriginCountryCodes = [...GALLERY_CARD_ORIGIN_COUNTRIES];
  const lockedCountryCode =
    cardOriginCountryCodes.length === 1 ? cardOriginCountryCodes[0] : "";
  const isCountryLocked = Boolean(lockedCountryCode);

  const [form, setForm] = useState<AddressFormFields>({
    street1: "123 Main St",
    street2: "",
    city: "Austin",
    state: "TX",
    zipCode: "78701",
    country: cardOriginCountryCodes.includes("US")
      ? "US"
      : (cardOriginCountryCodes[0] ?? "US"),
  });
  const [errors, setErrors] = useState<AddressFormErrors>({});

  const selectedCountryCode = isCountryLocked ? lockedCountryCode : form.country;
  const isUS = selectedCountryCode === "US";
  const { zipLocation, zipUnknown } = useUsBillingZip(isUS, form.zipCode);
  const canSubmit = isUS
    ? Boolean(form.street1.trim()) && Boolean(zipLocation)
    : true;

  const colorsConfig = theme?.whiteView;
  const { backgroundBtn, textColorBtn, bodyCopy, title } = colorsConfig;
  const fieldColors = {
    background: colorsConfig.background,
    title: colorsConfig.title,
    bodyCopy,
    inactiveColor: colorsConfig.subtitile,
    errorColor: colorsConfig.errorColor,
  };
  const errorColor = theme.errorView?.title ?? title;

  const handleChange = (field: keyof AddressFormFields, value: string) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "country" && value !== prev.country) {
        next.state = "";
      }
      return next;
    });
    if (errors[field as keyof AddressFormErrors]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validate = (): AddressFormErrors => {
    const errs: AddressFormErrors = {};
    if (!form.street1.trim()) errs.street1 = t("billingAddress.streetRequired");
    if (!isUS) {
      if (!form.city.trim()) errs.city = t("billingAddress.cityRequired");
      if (!form.state.trim()) errs.state = t("billingAddress.stateRequired");
    }
    if (isUS) {
      if (!/^\d{5}(-\d{4})?$/.test(form.zipCode)) {
        errs.zipCode = t("billingAddress.zipRequired");
      } else if (!zipLocation) {
        errs.zipCode = t("billingAddress.zipNotFound");
      }
    } else if (!form.zipCode.trim() || form.zipCode.trim().length < 2) {
      errs.zipCode = t("billingAddress.postalCodeRequired");
    }
    if (
      !selectedCountryCode ||
      !cardOriginCountryCodes.includes(selectedCountryCode)
    ) {
      errs.country = t("billingAddress.countryRequired");
    }
    return errs;
  };

  const handleSubmit = (event?: React.FormEvent) => {
    event?.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    navigate(LOLA_ROUTES.ADD_CARD_PROCESSING);
  };
  const saveArmed = useRef(false);
  const pressSave = (event?: React.MouseEvent<HTMLButtonElement>) => {
    const fromKeyboard = event?.detail === 0;
    if (!fromKeyboard && !saveArmed.current) return;
    saveArmed.current = false;
    handleSubmit();
  };

  const errorStyle: React.CSSProperties = {
    color: errorColor,
    fontSize: "12px",
    marginTop: "4px",
  };

  return (
    <AuraLayout colorConfig={colorsConfig}>
      <Layout.Header>
        <Navbar
          align="center"
          color={TITLE_COLOR}
          title={t("billingAddress.title")}
          onBackClick={() => navigate(-1)}
        />
      </Layout.Header>

      <Layout.Content className="collect">
        <ScrollFadeIndicator color={bodyCopy} />
        <form
          id="lola-billing-address-form"
          className="mock-card-form"
          autoComplete="on"
          onSubmit={handleSubmit}
        >
          <section className="mock-card-fields">
            <div className="mock-card-field">
                            <FloatingField
                label={t("billingAddress.fields.street1")}
                value={form.street1}
                required
                isValid={!errors.street1}
                colors={fieldColors}
                icon={<HomeAddressIcon colors={colorsConfig.dropzoneColors} size={18} />}
              >
<input
                id="lola-billing-street1"
                style={{ color: bodyCopy }}
                name="street1"
                autoComplete="address-line1"
                value={form.street1}
                onChange={(e) => handleChange("street1", e.target.value)}
                autoFocus
              />
              </FloatingField>
              {errors.street1 && <div style={errorStyle}>{errors.street1}</div>}
            </div>

            {!isUS && (
            <>
            <div className="mock-card-field">
              <FloatingField
                label={t("billingAddress.fields.state")}
                value={form.state}
                required
                isValid={!errors.state}
                colors={fieldColors}
              >
                {isUS ? (
                  <select
                    id="lola-billing-state"
                    style={{ color: bodyCopy }}
                    value={form.state}
                    onChange={(e) => handleChange("state", e.target.value)}
                  >
                    <option value="" />
                    {US_STATES.map((s) => (
                      <option key={s.code} value={s.code}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    id="lola-billing-state"
                    style={{ color: bodyCopy }}
                    name="state"
                    autoComplete="address-level1"
                    value={form.state}
                    onChange={(e) => handleChange("state", e.target.value)}
                  />
                )}
              </FloatingField>
              {errors.state && <div style={errorStyle}>{errors.state}</div>}
            </div>

            <div className="mock-card-field">
              <FloatingField
                label={t("billingAddress.fields.city")}
                value={form.city}
                required
                isValid={!errors.city}
                colors={fieldColors}
              >
                <input
                  id="lola-billing-city"
                  style={{ color: bodyCopy }}
                  name="city"
                  autoComplete="address-level2"
                  value={form.city}
                  onChange={(e) => handleChange("city", e.target.value)}
                />
              </FloatingField>
              {errors.city && <div style={errorStyle}>{errors.city}</div>}
            </div>
            </>
            )}

            <div className="mock-card-field">
              <FloatingField
                label={t("billingAddress.fields.postalCode")}
                value={form.zipCode}
                required
                isValid={!errors.zipCode}
                colors={fieldColors}
              >
                <input
                  id="lola-billing-postal"
                  style={{ color: bodyCopy }}
                  name="postalCode"
                  autoComplete="postal-code"
                  inputMode={isUS ? "numeric" : "text"}
                  maxLength={10}
                  value={form.zipCode}
                  onChange={(e) =>
                    handleChange(
                      "zipCode",
                      isUS
                        ? e.target.value.replace(/[^\d-]/g, "")
                        : e.target.value,
                    )
                  }
                />
              </FloatingField>
              {(errors.zipCode || zipUnknown) && (
                <div style={errorStyle}>
                  {errors.zipCode ?? t("billingAddress.zipNotFound")}
                </div>
              )}
              {!errors.zipCode && zipLocation && (
                <div style={{ color: bodyCopy, fontSize: "12px", marginTop: "4px" }}>
                  {t("billingAddress.zipLocation", {
                    city: zipLocation.city,
                    stateCode: zipLocation.stateCode,
                  })}
                </div>
              )}
            </div>

            {!isCountryLocked && (
              <div className="mock-card-field">
                <FloatingField
                  label={t("billingAddress.fields.countryCode")}
                  value={form.country}
                  required
                  isValid={!errors.country}
                  colors={fieldColors}
                >
                  <select
                    id="lola-billing-country"
                    style={{ color: bodyCopy }}
                    value={form.country}
                    onChange={(e) => handleChange("country", e.target.value)}
                  >
                    {cardOriginCountryCodes.map((countryCode) => (
                      <option key={countryCode} value={countryCode}>
                        {countryDisplayName(countryCode, i18n.language || "en")}
                      </option>
                    ))}
                  </select>
                </FloatingField>
                {errors.country && (
                  <div style={errorStyle}>{errors.country}</div>
                )}
              </div>
            )}
          </section>
        </form>
      </Layout.Content>

      <Layout.Footer>
        <section className="valid-cards billing-address-footer">
          <section className="footer-section">
            <Button
              showIcon={false}
              size="large"
              disabled={!canSubmit}
              color={textColorBtn}
              background={backgroundBtn}
              onPointerDown={() => {
                saveArmed.current = true;
              }}
              onClick={pressSave}
            >
              {t("billingAddress.continue")}
            </Button>
          </section>
        </section>
      </Layout.Footer>
    </AuraLayout>
  );
});
